import { useState, useEffect } from 'react';
import { inventoryApi } from '../../services/inventoryApi';
import { InventoryComments } from './InventoryComments';
import { InventoryClassBadge } from './InventoryClassBadge';
import { OpenOrdersCard } from './OpenOrdersCard';
import type { InventoryItemDetail as DetailType } from '../../types/inventory';

interface InventoryDetailProps {
    itemId: string;
    canEdit: boolean;
    onCommentAdded: () => void;
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH');
}

function formatNumber(num: number | null | undefined): string {
    if (num == null) return '-';
    return Math.round(num).toLocaleString('de-CH');
}

function formatCurrency(num: number | null | undefined): string {
    if (num == null) return '-';
    return new Intl.NumberFormat('de-CH', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
    }).format(num);
}

function formatPricePerKg(num: number | null | undefined): string {
    if (num == null) return '—';
    return `${new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num)} EUR/kg`;
}

function daysAgo(days: number | null): string {
    if (days == null) return '';
    if (days === 0) return '(today)';
    if (days === 1) return '(1 day ago)';
    return `(${days} days ago)`;
}

const COVERAGE_THRESHOLDS = [
    { max: 3, label: '0-3', color: 'bg-green-500', faded: 'bg-green-200' },
    { max: 6, label: '3-6', color: 'bg-lime-500', faded: 'bg-lime-200' },
    { max: 12, label: '6-12', color: 'bg-yellow-500', faded: 'bg-yellow-200' },
    { max: 24, label: '12-24', color: 'bg-orange-500', faded: 'bg-orange-200' },
    { max: Infinity, label: '> 24', color: 'bg-red-500', faded: 'bg-red-200' },
];

function StockCoverageCard({ stockQuantity, rolling12MSalesQty, stockCoverageMonths, inventoryClass }: {
    stockQuantity: number;
    rolling12MSalesQty: number | null;
    stockCoverageMonths: number | null;
    inventoryClass: number;
}) {
    const isNoMover = inventoryClass === 6;

    return (
        <div className="rounded-md border border-gray-200 bg-gray-50/50 p-3">
            <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Stock Coverage</h5>
            <dl className="space-y-1">
                <div>
                    <dt className="text-xs text-gray-500">12M Sales Qty</dt>
                    <dd className="text-sm font-medium text-gray-900">
                        {isNoMover ? <span className="text-red-600">No sales</span> : formatNumber(rolling12MSalesQty)}
                    </dd>
                </div>
                <div>
                    <dt className="text-xs text-gray-500">Stock Qty</dt>
                    <dd className="text-sm text-gray-900">{formatNumber(stockQuantity)}</dd>
                </div>
                <div>
                    <dt className="text-xs text-gray-500">Coverage</dt>
                    <dd className="text-sm font-semibold text-gray-900">
                        {isNoMover
                            ? <span className="text-red-600">No Mover</span>
                            : stockCoverageMonths != null
                                ? <span>~{Math.round(stockCoverageMonths)} months</span>
                                : '-'
                        }
                    </dd>
                </div>
            </dl>
            {/* Coverage bar */}
            {!isNoMover && stockCoverageMonths != null && (
                <div className="mt-2">
                    <div className="flex items-center gap-0.5">
                        {COVERAGE_THRESHOLDS.map((t, i) => {
                            const min = i === 0 ? 0 : COVERAGE_THRESHOLDS[i - 1].max;
                            const isActive = stockCoverageMonths > min && (i === COVERAGE_THRESHOLDS.length - 1 || stockCoverageMonths <= t.max);
                            return (
                                <div
                                    key={t.label}
                                    className={`flex-1 rounded-sm ${isActive ? `${t.color} h-2.5` : `${t.faded} h-1.5`}`}
                                    title={`${t.label} months`}
                                />
                            );
                        })}
                    </div>
                    <p className="mt-1 text-[10px] text-gray-400">
                        {formatNumber(stockQuantity)} stock / {formatNumber(rolling12MSalesQty)} annual sales
                    </p>
                </div>
            )}
        </div>
    );
}

export function InventoryDetail({ itemId, canEdit, onCommentAdded }: InventoryDetailProps) {
    const [detail, setDetail] = useState<DetailType | null>(null);
    const [loading, setLoading] = useState(true);

    const loadDetail = () => {
        setLoading(true);
        inventoryApi.get(itemId)
            .then(setDetail)
            .catch(err => console.error('Failed to load detail:', err))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadDetail(); }, [itemId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            </div>
        );
    }

    if (!detail) {
        return <p className="py-4 text-sm text-gray-500">Failed to load item details.</p>;
    }

    return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Product Info */}
                    <div className="rounded-md border border-gray-200 bg-gray-50/50 p-3">
                        <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Product</h5>
                        <dl className="space-y-1">
                            <div><dt className="text-xs text-gray-500">Code</dt><dd className="text-sm font-medium text-gray-900">{detail.ProductCode}</dd></div>
                            <div><dt className="text-xs text-gray-500">Name</dt><dd className="text-sm text-gray-900">{detail.ProductName}</dd></div>
                            <div><dt className="text-xs text-gray-500">Supplier</dt><dd className="text-sm text-gray-900">{detail.SupplierName} ({detail.SupplierCode})</dd></div>
                            <div>
                                <dt className="text-xs text-gray-500">Classification</dt>
                                <dd className="mt-0.5"><InventoryClassBadge inventoryClass={detail.InventoryClass} /></dd>
                            </div>
                        </dl>
                    </div>

                    {/* Stock Info */}
                    <div className="rounded-md border border-gray-200 bg-gray-50/50 p-3">
                        <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Stock</h5>
                        <dl className="space-y-1">
                            <div><dt className="text-xs text-gray-500">Quantity</dt><dd className="text-sm font-medium text-gray-900">{formatNumber(detail.StockQuantity)}</dd></div>
                            <div><dt className="text-xs text-gray-500">Value EUR</dt><dd className="text-sm font-semibold text-blue-700">{formatCurrency(detail.StockValueEUR)}</dd></div>
                            <div><dt className="text-xs text-gray-500">Price / kg</dt><dd className="text-sm text-gray-900">{formatPricePerKg(detail.PricePerKgEUR)}</dd></div>
                            <div><dt className="text-xs text-gray-500">Oldest Batch</dt><dd className="text-sm text-gray-900">{detail.OldestBatchDate ? formatDate(detail.OldestBatchDate) : '—'}</dd></div>
                            <div><dt className="text-xs text-gray-500">Warehouse</dt><dd className="text-sm text-gray-900">{detail.WarehouseName} ({detail.WarehouseCode})</dd></div>
                            <div><dt className="text-xs text-gray-500">Country</dt><dd className="text-sm text-gray-900">{detail.WarehouseCountry || '-'}</dd></div>
                        </dl>
                    </div>

                    {/* Stock Coverage */}
                    <StockCoverageCard
                        stockQuantity={detail.StockQuantity}
                        rolling12MSalesQty={detail.Rolling12MSalesQty}
                        stockCoverageMonths={detail.StockCoverageMonths}
                        inventoryClass={detail.InventoryClass}
                    />

                    {/* Latest Activity */}
                    <div className="rounded-md border border-gray-200 bg-gray-50/50 p-3">
                        <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Latest Activity</h5>
                        <dl className="space-y-1">
                            <div>
                                <dt className="text-xs text-gray-500">Last Transaction</dt>
                                <dd className="text-sm text-gray-900">
                                    {detail.LatestTransactionType || '-'} {formatDate(detail.LatestTransactionDate)}
                                    <span className="ml-1 text-xs text-gray-400">{daysAgo(detail.DaysSinceLastTransaction)}</span>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>

                <OpenOrdersCard itemId={itemId} companyCode={detail.CompanyCode} />
            </div>

            <div>
                <InventoryComments
                    itemId={itemId}
                    comments={detail.comments}
                    canEdit={canEdit}
                    onCommentAdded={() => { loadDetail(); onCommentAdded(); }}
                />
            </div>
        </div>
    );
}
